import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {ApiError, apiErrorMessage, isApiError} from '../lib/apiError';
import {AudioUpload} from '../domain/types';
import {downloadText, pickFiles, AUDIO_ACCEPT} from '../lib/browserFiles';
import {
  PickedAudioFile,
  fileFromRememberedHandle,
  pickAudioFilesWithHandles,
  SHOW_PICKER_AUDIO_TYPES,
} from '../lib/fileHandles';
import {formatBytes, formatDurationCompact} from '../lib/audioMetadata';

import {useOwnedProjectId} from './useOwnedProjectId';
import {
  audioIsLoadingSelector,
  uploadRequestIdSelector,
  uploadingAudioIdSelector,
  uploadProgressSelector,
} from '../store/selectors/audio.selectors';
import {transcriptionQuotaSelector} from '../store/selectors/usage.selectors';
import {isProSelector} from '../store/selectors/entitlement.selectors';
import {myWordlistsSelector} from '../store/selectors/wordlists.selectors';
import {
  videoEditorSelector,
  prefersFullMediaPathSelector,
} from '../store/selectors/auth.selectors';
import {
  workspaceRowsSelector,
  orphanTranscriptionsSelector,
  WorkspaceRow,
} from '../store/selectors/workspace.selectors';

import {fetchAudioUploads, deleteAudioUpload} from '../store/slices/audioThunks';
import {fetchTranscriptions, downloadTranscriptionCsv} from '../store/slices/transcriptionsThunks';
import {
  fetchConversions,
  fetchProjectConversions,
  convertCsvToXml,
  downloadConversionXml,
} from '../store/slices/conversionsThunks';
import {fetchUserWordlists, fetchUserWordlistsCollection} from '../store/slices/wordlistsThunks';
import {fetchTranscriptionUsage} from '../store/slices/usageThunks';
import {allowanceForPlan} from '../domain/plans';

import UploadDropzone from './workspace/UploadDropzone';
import AudioFileTable from './workspace/AudioFileTable';
import TranscribeModal from './workspace/TranscribeModal';
import NewConversionModal, {ConversionSubmission} from './workspace/NewConversionModal';
import TranscriptModal from './workspace/TranscriptModal';
import UpgradeRequiredModal from './UpgradeRequiredModal';
import SessionLimitModal from './workspace/SessionLimitModal';
import ResumeUploadModal, {ResumeRefusalKind} from './workspace/ResumeUploadModal';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';
import {useAudioUploadQueue} from './workspace/useAudioUploadQueue';
import {useTranscriptionLaunch} from './workspace/useTranscriptionLaunch';

/** UI wording → the API's marker mode. Not one-to-one, so it is spelled out rather than inferred. */
const MARKER_MODE = {
  none: null,
  markers: 'clean',
  'markers-with-word': 'with-word',
} as const;

/**
 * The workspace: one list of audio files, and every action on a file lives in its row.
 *
 * This replaces the desktop app's three-screen march — upload here, transcribe there, convert
 * somewhere else, then go find the result in a fourth list. That shape made sense when a local
 * queue owned the work; on the web it left the user reconstructing which CSV came from which
 * recording. Here the file is the unit of work: it carries its transcription's state and the
 * conversions produced from it, and the only metered step (transcription) is the only one that asks
 * for confirmation.
 */
/**
 * The refusals that are about the word-list selection, and so belong in the convert dialog rather
 * than a toast. `wordlist_not_found` is the one that happens in practice; the other two are
 * backstops for a cap the picker already enforces and ids it only ever takes from the list endpoint.
 */
const WORDLIST_REFUSALS = ['wordlist_not_found', 'too_many_wordlists', 'invalid_wordlist_ids'];

const WorkspaceContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const {projectId: routeProjectId} = useParams<{projectId: string}>();
  // Only a project this account owns reaches the API — a route param is untrusted input, and one
  // left over from another account is answered with a 403. See `useOwnedProjectId`.
  const {projectId, project, isResolving: isResolvingProject} = useOwnedProjectId(routeProjectId);
  const rows = useAppSelector(workspaceRowsSelector);
  const orphanTranscriptions = useAppSelector(orphanTranscriptionsSelector);
  const isLoadingAudio = useAppSelector(audioIsLoadingSelector);
  const uploadRequestId = useAppSelector(uploadRequestIdSelector);
  const uploadingAudioId = useAppSelector(uploadingAudioIdSelector);
  const uploadProgress = useAppSelector(uploadProgressSelector);
  const quota = useAppSelector(transcriptionQuotaSelector);
  const isPro = useAppSelector(isProSelector);
  const maxFileBytes = allowanceForPlan(isPro).maxFileBytes;
  const wordLists = useAppSelector(myWordlistsSelector);
  const videoEditor = useAppSelector(videoEditorSelector);
  const prefersFullMediaPath = useAppSelector(prefersFullMediaPathSelector);

  const [search, setSearch] = useState('');
  const [transcribeRow, setTranscribeRow] = useState<WorkspaceRow | null>(null);
  const [conversionRow, setConversionRow] = useState<WorkspaceRow | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  // A convert refused over the word lists themselves — the server's own sentence plus, for a
  // `wordlist_not_found`, the ids at fault. Held rather than toasted so the dialog stays open with
  // the offending lists marked: the fix is a deselection in the picker, and a toast that vanishes
  // in six seconds cannot point at one.
  const [wordListError, setWordListError] = useState<{message: string; missing: string[]} | null>(null);
  const [rowToDelete, setRowToDelete] = useState<WorkspaceRow | null>(null);
  // A resume the server would not accept. Held rather than toasted because both refusals need an
  // offer attached — and the mismatch offer *is* the file they just picked, so it has to survive.
  const [resumeRefusal, setResumeRefusal] = useState<
    {
      kind: ResumeRefusalKind;
      row: WorkspaceRow;
      picked: PickedAudioFile;
      message: string;
      error: ApiError;
    } | null
  >(null);
  const [isClearingStalledUpload, setIsClearingStalledUpload] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [busyConversionId, setBusyConversionId] = useState<string | null>(null);
  const [upgradeError, setUpgradeError] = useState<ApiError | null>(null);
  const [transcript, setTranscript] = useState<
    {fileName: string; csv: string | null; isLoading: boolean; error: string | null} | null
  >(null);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const refreshLists = useCallback(() => {
    // Ownership of the requested project is still unknown. Firing now would either send an id we
    // cannot vouch for or load the unscoped list and immediately replace it.
    if (isResolvingProject) {
      return;
    }
    dispatch(fetchAudioUploads(projectId || 'all'));
    dispatch(fetchTranscriptions(projectId || 'all'));
    if (projectId) {
      dispatch(fetchProjectConversions(projectId));
    } else {
      dispatch(fetchConversions());
    }
  }, [dispatch, projectId, isResolvingProject]);

  useEffect(() => {
    refreshLists();
    // Both: the user's own lists and the collection they've added to. Neither is the whole set.
    dispatch(fetchUserWordlists());
    dispatch(fetchUserWordlistsCollection());
    dispatch(fetchTranscriptionUsage());
  }, [refreshLists, dispatch]);

  // A finished job turns a row from "Transcribing…" into "Ready", which changes what the row can
  // do — the poller updates the transcription itself, but the conversions list has to catch up too.
  const processingCount = rows.filter(row => row.activeTranscription?.status === 'processing').length;
  const previousProcessingCount = useRef(processingCount);
  useEffect(() => {
    if (processingCount < previousProcessingCount.current) {
      refreshLists();
    }
    previousProcessingCount.current = processingCount;
  }, [processingCount, refreshLists]);

  const handleUploaded = useCallback((audio: AudioUpload) => {
    showToast(`${audio.name} uploaded — ready to transcribe`, 'success');
    dispatch(fetchAudioUploads(projectId || 'all'));
  }, [showToast, dispatch, projectId]);

  // A stopped upload leaves a resumable row that has to become visible, and a resume that 404s
  // leaves a row that has to stop being. Both are the same fix: ask the server what is actually there.
  const handleUploadListStale = useCallback(() => {
    dispatch(fetchAudioUploads(projectId || 'all'));
  }, [dispatch, projectId]);

  const handleResumeRefused = useCallback(({audio, picked, error}: {
    audio: AudioUpload;
    picked: PickedAudioFile;
    error: ApiError;
  }) => {
    const row = rows.find(candidate => candidate.audio.id === audio.id);
    if (!row) {
      showToast(error.message, 'error', 6000);
      return;
    }
    // `fileMismatch` wins when both are set: which file is in hand is the more actionable of the
    // two, and it is the one with a recoverable path.
    setResumeRefusal({
      kind: error.fileMismatch ? 'mismatch' : 'dead',
      row,
      picked,
      message: error.message,
      error,
    });
  }, [rows, showToast]);

  const {uploadFiles, resumeUpload, cancel: cancelUpload, uploadState, isUploading} = useAudioUploadQueue({
    projectId,
    maxFileBytes,
    onUploaded: handleUploaded,
    onError: message => showToast(message, 'error', 6000),
    onPaymentRequired: setUpgradeError,
    onUploadListStale: handleUploadListStale,
    onResumeRefused: handleResumeRefused,
  });

  const {
    isStarting,
    start: launchTranscription,
    refused: refusedTranscription,
    dismissRefused,
    queueRefused,
    cancelQueued,
  } = useTranscriptionLaunch({
    projectId: projectId ?? null,
    onPaymentRequired: setUpgradeError,
    onLaunched: () => setTranscribeRow(null),
  });

  // Never reaches for the zone's own button — while an upload is running that button is Cancel, and
  // "Upload audio" must never be the thing that aborts the upload it is adding to. It opens a picker
  // directly, falling back to the zone's hidden input only where the handle-capable one is missing.
  const openFilePicker = useCallback(async () => {
    if (isUploading) {
      showToast('Wait for the current upload to finish first.', 'info');
      return;
    }

    // Same preference as the dropzone's own button: the picker that yields handles when there is
    // one, so an upload started from the header is just as resumable without re-picking.
    const picked = await pickAudioFilesWithHandles(SHOW_PICKER_AUDIO_TYPES, true);

    if (picked === null) {
      dropzoneRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
      return;
    }
    if (picked.length > 0) {
      void uploadFiles(picked);
    }
  }, [isUploading, showToast, uploadFiles]);

  // The header's "Upload audio" button lives outside this page, so it asks for the picker through
  // the store rather than reaching for a ref it cannot have.
  const lastUploadRequest = useRef(uploadRequestId);
  useEffect(() => {
    if (uploadRequestId !== lastUploadRequest.current) {
      lastUploadRequest.current = uploadRequestId;
      void openFilePicker();
    }
  }, [uploadRequestId, openFilePicker]);

  /**
   * Resume, from the click that starts it.
   *
   * Both the permission prompt on a remembered handle and `showOpenFilePicker` need the browser to
   * still see the user's gesture, and that window is a few seconds rather than a single tick — so
   * the IndexedDB lookup in front of them is fine, but nothing slower may go there. In particular
   * this must not wait on a network call before opening a picker.
   */
  const handleResumeUpload = useCallback(async (row: WorkspaceRow) => {
    if (isUploading) {
      showToast('Wait for the current upload to finish first.', 'info');
      return;
    }

    // Chromium only, and only when this browser is the one that started the upload. Everywhere
    // else this is null and the picker below does the job for one extra click.
    const remembered = await fileFromRememberedHandle(row.audio.id);

    if (remembered) {
      void resumeUpload(row.audio, {file: remembered});
      return;
    }

    const picked = await pickAudioFilesWithHandles(SHOW_PICKER_AUDIO_TYPES, false);

    if (picked === null) {
      // No `showOpenFilePicker` here (Firefox, Safari). The plain input is the fallback, and it
      // gives back no handle — resume works, it just asks again next time.
      const files = await pickFiles({accept: AUDIO_ACCEPT});
      if (files?.[0]) {
        void resumeUpload(row.audio, {file: files[0]});
      }
      return;
    }

    if (picked.length > 0) {
      void resumeUpload(row.audio, picked[0]);
    }
  }, [isUploading, showToast, resumeUpload]);

  /** Mismatch: the file they picked is a different recording, so it becomes an upload of its own. */
  const handleStartNewUploadFromRefusal = useCallback(() => {
    const refusal = resumeRefusal;
    setResumeRefusal(null);
    if (refusal) {
      void uploadFiles([refusal.picked]);
    }
  }, [resumeRefusal, uploadFiles]);

  /** Not resumable: clear the stranded row, then let them pick the file again from scratch. */
  const handleDeleteAndReupload = useCallback(async () => {
    const refusal = resumeRefusal;
    if (!refusal) {
      return;
    }

    setIsClearingStalledUpload(true);
    const result = await dispatch(deleteAudioUpload(refusal.row.audio.id));
    setIsClearingStalledUpload(false);
    setResumeRefusal(null);

    if (!deleteAudioUpload.fulfilled.match(result)) {
      showToast(result.payload?.message || 'Could not delete that upload', 'error');
      return;
    }

    // They already have the file in hand from the resume attempt, so this goes straight to an
    // upload rather than sending them back to the picker for the third time.
    void uploadFiles([refusal.picked]);
  }, [resumeRefusal, dispatch, showToast, uploadFiles]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? rows.filter(row => row.audio.name.toLowerCase().includes(query)) : rows;
  }, [rows, search]);

  const handleStartTranscription = (params: {outputFileName: string; languageCode: string}) => {
    if (!transcribeRow) {
      return;
    }
    void launchTranscription(transcribeRow, params);
  };

  const handleRequestConversion = (row: WorkspaceRow) => {
    // A previous file's refused lists must not carry into this dialog: the marks would point at
    // ids this row never selected.
    setWordListError(null);
    setConversionRow(row);
  };

  const handleCancelConversion = () => {
    setConversionRow(null);
    setWordListError(null);
  };

  const handleConvert = async (submission: ConversionSubmission) => {
    const row = conversionRow;
    const transcription = row?.activeTranscription;

    if (!row || !transcription?.csvR2Key) {
      return;
    }

    // The audio record is the better source: the browser probed both at upload time, while the
    // transcription row only carries a sample rate on the rows that happen to have one.
    const sampleRate = row.audio.sampleRate ?? transcription.sampleRate;
    const duration = row.audio.duration ?? transcription.duration;

    if (sampleRate === undefined || duration === undefined) {
      showToast('This file has no sample rate or duration recorded, which the converter needs.', 'error', 6000);
      return;
    }

    // Both names are required by the dialog, so this is a guard rather than a fallback: an XML
    // whose clip names no file at all cannot be relinked in any editor, and quietly substituting a
    // name the user never typed would produce exactly the file they were trying to avoid.
    const audioPath = submission.audioPath.trim();

    if (!submission.outputFileName.trim() || !audioPath) {
      showToast('A conversion needs both an output filename and the source audio file name.', 'error', 6000);
      return;
    }

    let outputFileName = submission.outputFileName.trim().replace(/\s+/g, '-');
    if (!outputFileName.toLowerCase().endsWith('.xml')) {
      outputFileName += '.xml';
    }

    setIsConverting(true);
    setWordListError(null);

    try {
      const result = await dispatch(convertCsvToXml({
        csvKey: transcription.csvR2Key,
        audioPath,
        audioSampleRate: sampleRate,
        audioDuration: duration,
        projectId: projectId || undefined,
        // Beep sounds are not available on the web yet, so conversions always cut to silence.
        mode: 'silence',
        markerMode: MARKER_MODE[submission.markerOption] as 'word' | 'clean' | null,
        wordlistIds: submission.wordListIds,
        fps: submission.fps,
        outputFileName,
      })).unwrap();

      showToast(
        `Conversion ready — ${outputFileName} · ${result.metadata.censored_words} words censored`,
        'success',
        6000
      );

      // "I added a list and the cut count didn't change" has an answer in the response, and this
      // is the only moment to give it: `added: 0` means every word that list carries was already
      // covered by one picked before it. Only worth saying when more than one list went in — a
      // single list that added nothing is an empty list, which is a different conversation.
      const listsUsed = result.metadata.wordlists ?? [];
      const contributedNothing = listsUsed.filter(list => list.added === 0);

      if (listsUsed.length > 1 && contributedNothing.length > 0) {
        showToast(
          `${contributedNothing.map(list => list.name).join(', ')} added no new words — every word `
          + 'they carry was already covered by another list you picked. '
          + `${result.metadata.censor_word_count} words were matched in total.`,
          'info',
          8000
        );
      }

      setConversionRow(null);
      // The new conversion belongs in the row it came from, immediately.
      if (projectId) {
        dispatch(fetchProjectConversions(projectId));
      } else {
        dispatch(fetchConversions());
      }
    } catch (error) {
      // The convert dialog already gates on this for a strict editor, but the server enforces it for
      // every editor regardless — this is the backstop for whatever slips past that client-side
      // check. `message` is written for the user, so it's shown verbatim rather than paraphrased.
      if (isApiError(error) && WORDLIST_REFUSALS.includes(error.reason ?? '')) {
        // The dialog stays open and the picker takes the explanation. A `wordlist_not_found` is
        // reachable in ordinary use — a list deleted since it was last picked is still sitting in
        // the default-list prefill — and it is now a refusal rather than the old silent "convert
        // with nothing censored", so the user has to be able to see which list to drop and retry.
        setWordListError({message: error.message, missing: error.missing ?? []});
      } else if (isApiError(error) && error.reason === 'missing_media_reference') {
        showToast(error.message, 'error', 8000);
      } else {
        showToast(`Conversion failed: ${apiErrorMessage(error)}`, 'error', 6000);
      }
    } finally {
      setIsConverting(false);
    }
  };

  const handleViewTranscript = async (row: WorkspaceRow) => {
    const transcription = row.activeTranscription;

    if (!transcription?.csvR2Key) {
      return;
    }

    setTranscript({fileName: `${transcription.name}.csv`, csv: null, isLoading: true, error: null});

    const result = await dispatch(downloadTranscriptionCsv({csvR2Key: transcription.csvR2Key}));

    if (downloadTranscriptionCsv.fulfilled.match(result)) {
      setTranscript({
        fileName: `${transcription.name}.csv`,
        csv: result.payload,
        isLoading: false,
        error: null,
      });
      return;
    }

    setTranscript({
      fileName: `${transcription.name}.csv`,
      csv: null,
      isLoading: false,
      error: 'Could not load this transcript. Try downloading the CSV instead.',
    });
  };

  const handleDownloadCsv = async (row: WorkspaceRow) => {
    const transcription = row.activeTranscription;

    if (!transcription?.csvR2Key) {
      return;
    }

    const result = await dispatch(downloadTranscriptionCsv({csvR2Key: transcription.csvR2Key}));

    if (downloadTranscriptionCsv.fulfilled.match(result)) {
      downloadText(result.payload, `${transcription.name}.csv`, 'text/csv;charset=utf-8');
      return;
    }

    showToast('Could not download that CSV', 'error');
  };

  const handleDownloadConversion = async (conversionId: string, fileName: string) => {
    setBusyConversionId(conversionId);

    const result = await dispatch(downloadConversionXml({conversionId}));

    setBusyConversionId(null);

    if (downloadConversionXml.fulfilled.match(result)) {
      downloadText(result.payload, fileName, 'application/xml;charset=utf-8');
      return;
    }

    showToast('Could not download that XML', 'error');
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) {
      return;
    }

    setIsDeleting(true);
    const result = await dispatch(deleteAudioUpload(rowToDelete.audio.id));
    setIsDeleting(false);

    if (deleteAudioUpload.fulfilled.match(result)) {
      showToast(`${rowToDelete.audio.name} deleted`, 'success');
    } else {
      showToast(result.payload?.message || 'Could not delete the audio file', 'error');
    }

    setRowToDelete(null);
  };

  const conversionBlockedReason = conversionRow
    && conversionRow.audio.sampleRate === undefined
    && conversionRow.activeTranscription?.sampleRate === undefined
    ? 'No sample rate was recorded for this audio, and the converter needs it. Re-upload the file to fix this.'
    : null;

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-50">
              {project ? project.name : 'Workspace'}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Upload audio, transcribe what you need to censor, then convert as often as you like.
            </p>
          </div>
        </div>

        <div ref={dropzoneRef}>
          <UploadDropzone
            onFilesPicked={uploadFiles}
            onCancel={cancelUpload}
            upload={uploadState}
            disabled={isUploading}
            // The duration cap leads, and reads as `and` rather than `or`: both ceilings are
            // enforced, and the one that actually refuses uploads is the duration — a 5-minute cap
            // is nothing like the balance shown in the header, so it has to be legible before the
            // file is picked rather than in the 402 afterwards.
            limitsLabel={
              (quota.maxFileSeconds !== null
                ? `Up to ${formatDurationCompact(quota.maxFileSeconds)} and ${formatBytes(maxFileBytes)} per file`
                : `Up to ${formatBytes(maxFileBytes)} per file`)
              + ' · WAV, MP3, M4A and more'
            }
          />
        </div>

        {quota.isKnown && quota.level !== 'ok' && (
          <p className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-sm text-amber-200">
            {quota.level === 'critical'
              ? 'You’re out of transcription time this period. Conversions still work on anything already transcribed.'
              // quota.secondsRemaining is only ever null when the plan is uncapped, and an uncapped
              // plan never reaches 'low' or 'critical' — see transcriptionQuotaSelector.
              : `You’re almost out of transcription time this period — ${formatDurationCompact(quota.secondsRemaining as number)} left.`}
          </p>
        )}

        <AudioFileTable
          rows={filteredRows}
          isLoading={isLoadingAudio}
          search={search}
          onSearchChange={setSearch}
          onUploadClick={() => { void openFilePicker(); }}
          secondsRemaining={quota.secondsRemaining ?? Infinity}
          isQuotaKnown={quota.isKnown}
          busyConversionId={busyConversionId}
          uploadingAudioId={uploadingAudioId}
          uploadPercent={uploadProgress.percent}
          isUploadBusy={isUploading}
          onTranscribe={setTranscribeRow}
          onNewConversion={handleRequestConversion}
          onViewTranscript={handleViewTranscript}
          onDownloadCsv={handleDownloadCsv}
          onDownloadConversion={handleDownloadConversion}
          onDelete={setRowToDelete}
          onCancelQueued={cancelQueued}
          onResumeUpload={(row) => { void handleResumeUpload(row); }}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <p className="max-w-2xl text-xs text-gray-600">
            Only transcribe files you plan to censor. Once a transcription exists you can create as
            many XML conversions as you need with different word lists — at no extra cost.
          </p>
          {orphanTranscriptions.length > 0 && (
            <Link
              to={projectId ? `/projects/${projectId}/transcriptions` : '/transcriptions'}
              className="text-xs text-indigo-400 hover:text-indigo-300"
            >
              {orphanTranscriptions.length} imported transcription
              {orphanTranscriptions.length === 1 ? '' : 's'} without an audio file →
            </Link>
          )}
        </div>
      </div>

      <TranscribeModal
        row={transcribeRow}
        quota={quota}
        isStarting={isStarting}
        onCancel={() => setTranscribeRow(null)}
        onConfirm={handleStartTranscription}
      />

      <NewConversionModal
        row={conversionRow}
        wordLists={wordLists}
        defaultWordListId={project?.defaultWordlistId}
        wordListError={wordListError?.message ?? null}
        missingWordListIds={wordListError?.missing ?? []}
        isConverting={isConverting}
        blockedReason={conversionBlockedReason}
        videoEditor={videoEditor}
        prefersFullMediaPath={prefersFullMediaPath}
        onCancel={handleCancelConversion}
        onConfirm={handleConvert}
      />

      <TranscriptModal
        isOpen={transcript !== null}
        fileName={transcript?.fileName ?? ''}
        csv={transcript?.csv ?? null}
        isLoading={transcript?.isLoading ?? false}
        error={transcript?.error ?? null}
        onClose={() => setTranscript(null)}
        onDownload={() => {
          if (transcript?.csv) {
            downloadText(transcript.csv, transcript.fileName, 'text/csv;charset=utf-8');
          }
        }}
      />

      <DeleteConfirmationModal
        isOpen={Boolean(rowToDelete)}
        listName={rowToDelete?.audio.name || null}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setRowToDelete(null)}
        title="Delete this audio file?"
        message="This removes the uploaded audio. Transcriptions and conversions already made from it are kept. Delete"
      />

      <UpgradeRequiredModal
        isOpen={upgradeError !== null}
        message={upgradeError?.message || ''}
        duration={upgradeError?.duration}
        limit={upgradeError?.limit ?? undefined}
        requiredPlan={upgradeError?.requiredPlan}
        reason={upgradeError?.reason}
        onClose={() => setUpgradeError(null)}
      />

      <ResumeUploadModal
        kind={resumeRefusal?.kind ?? null}
        originalName={resumeRefusal?.row.audio.name ?? ''}
        pickedName={resumeRefusal?.picked.file.name ?? ''}
        message={resumeRefusal?.message ?? ''}
        expectedSize={resumeRefusal?.error.expectedSize}
        pickedSize={resumeRefusal?.picked.file.size ?? 0}
        isBusy={isClearingStalledUpload}
        onStartNewUpload={handleStartNewUploadFromRefusal}
        onDeleteAndReupload={() => { void handleDeleteAndReupload(); }}
        onCancel={() => setResumeRefusal(null)}
      />

      <SessionLimitModal
        error={refusedTranscription?.error ?? null}
        fileName={refusedTranscription?.row.audio.name ?? null}
        periodSecondsRemaining={quota.secondsRemaining}
        releaseAt={refusedTranscription?.releaseAt ?? ''}
        onQueue={queueRefused}
        onClose={dismissRefused}
      />
    </div>
  );
};

export default WorkspaceContainer;
